import { useQuery } from '@apollo/client/react';

import type { GetFilterType } from '~/shared/apollo-queries';
import { courseCategoryFragment } from '~/shared/graphql-queries/course-categories';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PaginationVariablesType } from '~/shared/types/general-type';

type PaginationCourseCategoryQueryType = Readonly<{
  paginationCourseCategoryQuery: {
    items: Record<string, unknown>[];
    pageInfo: {
      hasPreviousPage: boolean;
      hasNextPage: boolean;
      currentPage: number;
      perPage: number;
      itemCount: number;
      pageCount: number;
    };
  };
}>;

type CourseCategoryFilterOptions = Readonly<{
  filter: GetFilterType;
  alias: string | string[] | null;
  nextPage?: number;
  perPage?: number;
}>;

type FetchMoreArgs = Readonly<{
  nextPage: number;
}>;

const CATEGORIES_PER_PAGE = 12;

const paginationCourseCategoryQueryDocument = itemsPagination({
  entity: 'courseCategory',
  fragment: courseCategoryFragment,
});

const fallbackData: PaginationCourseCategoryQueryType = {
  paginationCourseCategoryQuery: {
    items: [],
    pageInfo: {
      hasPreviousPage: false,
      hasNextPage: false,
      currentPage: 1,
      perPage: 1,
      itemCount: 0,
      pageCount: 1,
    },
  },
};

export const usePaginationCourseCategoryQuery = (
  filterOptions: CourseCategoryFilterOptions,
  options: Record<string, unknown> = {},
) => {
  const resolvedPerPage =
    filterOptions.perPage ?? (filterOptions.nextPage === 1 ? CATEGORIES_PER_PAGE + 1 : CATEGORIES_PER_PAGE);

  const queryResult = useQuery<PaginationCourseCategoryQueryType, PaginationVariablesType>(
    paginationCourseCategoryQueryDocument,
    {
      variables: {
        filter: filterOptions.filter,
        nextPage: filterOptions.nextPage ?? 1,
        sort: '_ID_ASC',
        perPage: resolvedPerPage,
      },
      ...options,
    },
  );

  const { error, data, loading, fetchMore, refetch: refetchQuery, networkStatus } = queryResult;

  const fetchMoreHandler = async ({ nextPage }: FetchMoreArgs) => {
    await fetchMore({
      variables: {
        filter: filterOptions.filter,
        nextPage,
        sort: '_ID_ASC',
        perPage: filterOptions.perPage ?? CATEGORIES_PER_PAGE,
      },
      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousQueryResult;
        }

        return {
          paginationCourseCategoryQuery: {
            items: [
              ...previousQueryResult.paginationCourseCategoryQuery.items,
              ...fetchMoreResult.paginationCourseCategoryQuery.items,
            ],
            pageInfo: fetchMoreResult.paginationCourseCategoryQuery.pageInfo,
          },
        };
      },
    });
  };

  return {
    error,
    loading,
    refetch: refetchQuery,
    networkStatus,
    data: data ?? fallbackData,
    fetchMore: fetchMoreHandler,
  };
};
